// src/components/user/controltower/ControlTower.jsx
import React, { useEffect, useRef } from 'react';
import { useControlTower } from './useControlTower';
import { TowerHeader }          from './TowerHeader';
import { TowerSidebar }         from './TowerSidebar';
import RegularDeliveryModal from './RegularDeliveryModal';
import { ExpressDeliveryModal } from './ExpressDeliveryModal';
import StatusFooter             from './StatusFooter';

// Lima district fallback coordinates
const DISTRICTS = {
  "Ancon":                    { latitude: -11.770, longitude: -77.155 },
  "Ate":                      { latitude: -12.037, longitude: -76.955 },
  "Barranco":                 { latitude: -12.145, longitude: -77.021 },
  "Breña":                    { latitude: -12.063, longitude: -77.049 },
  "Carabayllo":               { latitude: -11.884, longitude: -77.038 },
  "Chorrillos":               { latitude: -12.178, longitude: -77.001 },
  "Chosica":                  { latitude: -11.937, longitude: -76.696 },
  "Comas":                    { latitude: -11.936, longitude: -77.060 },
  "El Agustino":              { latitude: -12.047, longitude: -76.997 },
  "Huaycan":                  { latitude: -12.016, longitude: -76.831 },
  "Independencia":            { latitude: -11.982, longitude: -77.049 },
  "Jesús María":              { latitude: -12.076, longitude: -77.049 },
  "La Molina":                { latitude: -12.073, longitude: -76.947 },
  "La Victoria":              { latitude: -12.071, longitude: -77.013 },
  "Lince":                    { latitude: -12.087, longitude: -77.035 },
  "Los Olivos":               { latitude: -11.981, longitude: -77.067 },
  "Lurigancho":               { latitude: -11.980, longitude: -76.840 },
  "Lurin":                    { latitude: -12.275, longitude: -76.872 },
  "Magdalena del Mar":        { latitude: -12.090, longitude: -77.069 },
  "Miraflores":               { latitude: -12.120, longitude: -77.030 },
  "Pachacamac":               { latitude: -12.232, longitude: -76.870 },
  "Pueblo Libre":             { latitude: -12.078, longitude: -77.073 },
  "Puente Piedra":            { latitude: -11.872, longitude: -77.069 },
  "Rimac":                    { latitude: -12.026, longitude: -77.026 },
  "San Borja":                { latitude: -12.101, longitude: -77.001 },
  "San Isidro":               { latitude: -12.096, longitude: -77.036 },
  "San Juan de Lurigancho":   { latitude: -12.006, longitude: -76.988 },
  "San Juan de Miraflores":   { latitude: -12.152, longitude: -76.963 },
  "San Luis":                 { latitude: -12.073, longitude: -77.000 },
  "San Martin de Porres":     { latitude: -12.015, longitude: -77.059 },
  "San Miguel":               { latitude: -12.080, longitude: -77.096 },
  "Santa Anita":              { latitude: -12.052, longitude: -76.971 },
  "Santa Rosa":               { latitude: -11.780, longitude: -77.170 },
  "Santiago de Surco":        { latitude: -12.140, longitude: -76.995 },
  "Surquillo":                { latitude: -12.116, longitude: -77.022 },
  "Villa El Salvador":        { latitude: -12.193, longitude: -76.949 },
  "Villa Maria del Triunfo":  { latitude: -12.161, longitude: -76.944 },
  "Cercado de Lima":          { latitude: -12.046, longitude: -77.031 },
  // Callao Province
  "Bellavista":               { latitude: -12.062, longitude: -77.115 },
  "Callao":                   { latitude: -12.055, longitude: -77.130 },
  "Carmen de la Legua":       { latitude: -12.052, longitude: -77.098 },
  "La Perla":                 { latitude: -12.071, longitude: -77.118 },
  "La Punta":                 { latitude: -12.077, longitude: -77.162 },
  "Ventanilla":               { latitude: -11.875, longitude: -77.130 },
};

// ── SVG data URIs for markers ─────────────────────────────────────────────────

// Regular delivery pins — colored circles (existing behavior preserved)
function regularPinUrl(status) {
  const colors = {
    pending:   '#F97316',
    encamino:  '#3B82F6',
    entregado: '#22C55E',
    cancelado: '#EF4444',
    rechazado: '#EF4444',
  };
  const color = colors[status] || '#94A3B8';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="12" fill="${color}" stroke="white" stroke-width="3"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Express delivery marker — lightning bolt shape in brand green
function expressMarkerUrl(status) {
  const colors = {
    pending:   '#F97316',
    encamino:  '#3B82F6',
    entregado: '#22C55E',
    cancelado: '#EF4444',
    rechazado: '#EF4444',
  };
  const statusColor = colors[status] || '#94A3B8';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
    <rect x="2" y="2" width="32" height="32" rx="8" fill="#67A43C" stroke="white" stroke-width="2"/>
    <polygon points="20,4 12,20 18,20 16,32 24,16 18,16" fill="white"/>
    <circle cx="28" cy="8" r="5" fill="${statusColor}" stroke="white" stroke-width="1.5"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Driver marker — regular (logo-style circle, brand primary)
function regularDriverMarkerUrl() {
  return `${process.env.PUBLIC_URL}/logo.png`;
}

// Driver marker — express (brand green)
function expressDriverMarkerUrl() {
  return `${process.env.PUBLIC_URL}/logo.png`;
}

// ─────────────────────────────────────────────────────────────────────────────

function ControlTower({ userId, setActiveComponent }) {
  const mapContainerRef  = useRef(null);
  const mapRef           = useRef(null);
  const deliveryMarkersRef = useRef({});
  const expressMarkersRef  = useRef({});
  const driverMarkersRef   = useRef({});
  const orderMarkersRef    = useRef({});
  const expressPolylineRef = useRef(null);
  const expressPolyMarkersRef = useRef([]);

  const tower = useControlTower(userId);

  // ── Init map ────────────────────────────────────────────────
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current && window.google) {
      mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: -12.0602, lng: -77.0371 },
        zoom: 12,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'greedy',
      });
    }
  }, []);

  // ── Regular delivery markers ────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    Object.values(deliveryMarkersRef.current).forEach(m => m.setMap(null));
    deliveryMarkersRef.current = {};

    tower.deliveryPoints.forEach(point => {
      let { latitude, longitude } = point;
      if (!latitude || !longitude) {
        const d = DISTRICTS[point.district];
        if (!d) return;
        latitude  = d.latitude  + (Math.random() - 0.5) * 0.01;
        longitude = d.longitude + (Math.random() - 0.5) * 0.01;
      }

      const marker = new window.google.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map: mapRef.current,
        icon: { url: regularPinUrl(point.status), scaledSize: new window.google.maps.Size(32, 32) },
        zIndex: 10,
      });

      marker.addListener('click', () => {
        tower.setSelectedDelivery({ type: 'regular', data: point });
        mapRef.current.panTo({ lat: latitude, lng: longitude });
        mapRef.current.setZoom(16);
      });

      deliveryMarkersRef.current[point.id] = marker;
    });
  }, [tower.deliveryPoints]);

  // ── Express delivery markers ────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    Object.values(expressMarkersRef.current).forEach(m => m.setMap(null));
    expressMarkersRef.current = {};

    tower.expressPoints.forEach(point => {
      if (!point.pickup_lat || !point.pickup_lng) return;

      const marker = new window.google.maps.Marker({
        position: { lat: point.pickup_lat, lng: point.pickup_lng },
        map: mapRef.current,
        icon: { url: expressMarkerUrl(point.status), scaledSize: new window.google.maps.Size(36, 36) },
        zIndex: 15,
      });

      marker.addListener('click', () => {
        tower.setSelectedDelivery({ type: 'express', data: point });
        drawExpressRoute(point);
      });

      expressMarkersRef.current[point.id] = marker;
    });
  }, [tower.expressPoints]);

  // ── Draw express polyline on selection ─────────────────────
  function drawExpressRoute(point) {
    // Clear previous polyline + markers
    if (expressPolylineRef.current) expressPolylineRef.current.setMap(null);
    expressPolyMarkersRef.current.forEach(m => m.setMap(null));
    expressPolyMarkersRef.current = [];

    if (!point.route_polyline || !window.google) return;

    const path = window.google.maps.geometry.encoding.decodePath(point.route_polyline);

    expressPolylineRef.current = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#67A43C',
      strokeOpacity: 0.85,
      strokeWeight: 5,
    });
    expressPolylineRef.current.setMap(mapRef.current);

    // Fit bounds
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    mapRef.current.fitBounds(bounds, 60);
  }

  // Clear polyline when selection changes away from express
  useEffect(() => {
    if (tower.selectedDelivery?.type !== 'express') {
      if (expressPolylineRef.current) expressPolylineRef.current.setMap(null);
      expressPolyMarkersRef.current.forEach(m => m.setMap(null));
      expressPolyMarkersRef.current = [];
    }
  }, [tower.selectedDelivery]);

  // ── Order (pickup) markers ──────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    Object.values(orderMarkersRef.current).forEach(m => m.setMap(null));
    orderMarkersRef.current = {};

    tower.orders.forEach(order => {
      const addr = tower.user.addresses?.find(a => a.id === order.address_id);
      if (!addr?.latitude || !addr?.longitude) return;
      const marker = new window.google.maps.Marker({
        position: { lat: addr.latitude, lng: addr.longitude },
        map: mapRef.current,
        icon: { url: `${process.env.PUBLIC_URL}/icons/store.svg`, scaledSize: new window.google.maps.Size(40, 40) },
        zIndex: 5,
      });
      orderMarkersRef.current[order.id] = marker;
    });
  }, [tower.orders, tower.user.addresses]);

  // ── Driver markers ──────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    Object.values(driverMarkersRef.current).forEach(m => m.setMap(null));
    driverMarkersRef.current = {};

    tower.drivers.forEach(driver => {
      if (!driver.latitude || !driver.longitude) return;
      const isExpress = tower.expressDriverIds.has(driver.id);

      const marker = new window.google.maps.Marker({
        position: { lat: driver.latitude, lng: driver.longitude },
        map: mapRef.current,
        icon: {
          url: isExpress ? expressDriverMarkerUrl() : regularDriverMarkerUrl(),
          scaledSize: new window.google.maps.Size(36, 36),
        },
        animation: window.google.maps.Animation.BOUNCE,
        zIndex: 20,
      });

      // Stop bouncing after 750ms — just one subtle bounce cycle
      setTimeout(() => {
        if (marker.getMap()) {
          marker.setAnimation(null);
        }
      }, 750);

      driverMarkersRef.current[driver.id] = marker;
    });
  }, [tower.drivers, tower.expressDriverIds]);

  // ── Handle delivery click from sidebar ─────────────────────
  function handleDeliveryClick(item) {
    tower.setSelectedDelivery(item);

    if (item.type === 'regular') {
      let lat = item.data.latitude;
      let lng = item.data.longitude;
      if (!lat || !lng) {
        const d = DISTRICTS[item.data.district];
        if (d) { lat = d.latitude; lng = d.longitude; }
      }
      if (lat && lng && mapRef.current) {
        mapRef.current.panTo({ lat, lng });
        mapRef.current.setZoom(16);
      }
    } else {
      drawExpressRoute(item.data);
    }
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">

      <TowerHeader
        setActiveComponent={setActiveComponent}
        showList={tower.showList}
        onToggleList={() => tower.setShowList(s => !s)}
        deliveryPoints={tower.deliveryPoints}
        expressPoints={tower.expressPoints}
      />

      <div className="flex-1 flex overflow-hidden relative">
        <TowerSidebar
          showList={tower.showList}
          activeTab={tower.activeTab}
          setActiveTab={tower.setActiveTab}
          searchQuery={tower.searchQuery}
          onSearchChange={tower.setSearchQuery}
          filteredRegular={tower.filteredRegular}
          filteredExpress={tower.filteredExpress}
          totalRegular={tower.deliveryPoints.length}
          totalExpress={tower.expressPoints.length}
          selectedDelivery={tower.selectedDelivery}
          onDeliveryClick={handleDeliveryClick}
        />

        {/* Map */}
        <div className="flex-1 relative">
          <div ref={mapContainerRef} className="w-full h-full" />
          {tower.loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <div className="w-10 h-10 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>

      <StatusFooter
        deliveryPoints={tower.deliveryPoints}
        expressPoints={tower.expressPoints}
      />

      {/* Modals */}
      {tower.selectedDelivery?.type === 'regular' && (
        <RegularDeliveryModal
          delivery={tower.selectedDelivery.data}
          onClose={() => tower.setSelectedDelivery(null)}
        />
      )}
      {tower.selectedDelivery?.type === 'express' && (
        <ExpressDeliveryModal
          delivery={tower.selectedDelivery.data}
          onClose={() => tower.setSelectedDelivery(null)}
        />
      )}
    </div>
  );
}

export default ControlTower;